import React, { useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { 
    LayoutDashboard, Package, Users, ShoppingCart, 
    LogOut, History, UserCog, Menu, X, Clock, Dumbbell, ShieldCheck, 
    Layers, CalendarCheck, Zap, ChevronDown, ChevronRight, PanelLeftClose, PanelLeftOpen 
} from 'lucide-react';
import useAuthStore from '../store/authStore';

const MainLayout = () => {
    const user = useAuthStore(state => state.user);
    const logout = useAuthStore(state => state.logout);
    const location = useLocation();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isCollapsed, setIsCollapsed] = useState(false);

    const isAdmin = user?.role === 'admin';

    const isForfaitsActive = location.pathname.startsWith('/forfaits');
    const isAbonnementsActive = isForfaitsActive && (!location.search || location.search.includes('tab=abonnements'));
    const isSeancesActive = isForfaitsActive && location.search.includes('tab=seances');

    // Menu "Forfaits" fermé par défaut, ne s'ouvre que si on est sur la page /forfaits
    const [forfaitsMenuOpen, setForfaitsMenuOpen] = useState(isForfaitsActive);

    const navItems = [
        { name: 'Tableau de bord', path: '/dashboard', icon: <LayoutDashboard size={20} /> },
        { name: 'Clients', path: '/clients', icon: <Users size={20} /> },
        { name: 'Abonnements', path: '/abonnements', icon: <CalendarCheck size={20} /> },
        { name: 'Séances', path: '/seances', icon: <Zap size={20} /> },
        {
            name: 'Forfaits',
            path: '/forfaits',
            icon: <Layers size={20} />,
            isExpandable: true,
            subItems: [
                { name: 'Forfaits Abonnements', path: '/forfaits?tab=abonnements', icon: <CalendarCheck size={16} />, isActive: isAbonnementsActive },
                { name: 'Forfaits Séances', path: '/forfaits?tab=seances', icon: <Zap size={16} />, isActive: isSeancesActive },
            ]
        },
        { name: 'Taekwondo Enfants', path: '/taekwondo', icon: <ShieldCheck size={20} /> },
        { name: 'Point de Vente', path: '/pos', icon: <ShoppingCart size={20} /> },
        { name: 'Pointage', path: '/pointage', icon: <Clock size={20} /> },
    ];

    if (isAdmin) {
        navItems.push(
            { name: 'Catalogue & Articles', path: '/catalog', icon: <Package size={20} /> },
            // { name: 'Paiements', path: '/payments', icon: <CreditCard size={20} /> },
            { name: 'Commandes en Ligne', path: '/online-orders', icon: <ShoppingCart size={20} /> },
            { name: 'Historique Ventes', path: '/sales-history', icon: <History size={20} /> },
            { name: 'Gestion Utilisateurs', path: '/users-management', icon: <UserCog size={20} /> },
            { name: 'Coachs', path: '/coaches', icon: <Dumbbell size={20} /> }
        );
    }

    const closeMobileMenu = () => setIsMobileMenuOpen(false);

    return (
        <div className="flex h-screen bg-brand-light font-sans overflow-hidden">
            {/* Mobile Overlay */}
            {isMobileMenuOpen && (
                <div
                    className="fixed inset-0 bg-black/60 backdrop-blur-xs z-40 md:hidden transition-opacity"
                    onClick={closeMobileMenu}
                />
            )}

            {/* Sidebar */}
            <aside
                className={`fixed md:static inset-y-0 left-0 z-50 bg-[#0d1117] text-gray-200 flex flex-col shadow-2xl border-r border-gray-800/80 transition-all duration-300 ease-in-out ${
                    isMobileMenuOpen ? 'translate-x-0 w-64' : '-translate-x-full md:translate-x-0'
                } ${isCollapsed ? 'md:w-20' : 'md:w-64'}`}
            >
                {/* Sidebar Header */}
                <div className="h-16 flex items-center justify-between px-4 border-b border-gray-800/80 bg-[#161b22]/70 shrink-0">
                    <div className="flex items-center gap-3 overflow-hidden">
                        <img 
                            src="/logohop_fitness.jpeg" 
                            alt="Logo HopFitness" 
                            className="w-10 h-10 rounded-xl object-contain bg-black border border-brand-primary/40 shadow-md shrink-0" 
                        />
                        {!isCollapsed && (
                            <span className="text-xl font-black tracking-tight text-white select-none whitespace-nowrap">
                                HOP<span className="text-brand-primary">FITNESS</span>
                            </span>
                        )}
                    </div>

                    <div className="flex items-center">
                        {/* Desktop Toggle Button */}
                        <button
                            type="button"
                            onClick={() => setIsCollapsed(!isCollapsed)}
                            className="hidden md:flex items-center justify-center w-8 h-8 rounded-lg text-gray-400 hover:text-white hover:bg-gray-800 transition-colors"
                            title={isCollapsed ? 'Agrandir la barre latérale' : 'Réduire la barre latérale'}
                        >
                            {isCollapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
                        </button>
                        {/* Mobile Close Button */}
                        <button
                            type="button"
                            onClick={closeMobileMenu}
                            className="md:hidden text-gray-400 hover:text-white p-1 rounded-lg hover:bg-gray-800"
                        >
                            <X size={22} />
                        </button>
                    </div>
                </div>

                {/* Sidebar Nav Items */}
                <div className="flex-1 overflow-y-auto py-4 px-2.5 custom-scrollbar">
                    <nav className="space-y-1.5">
                        {navItems.map((item: any) => {
                            if (item.isExpandable) {
                                const isParentActive = isForfaitsActive;

                                return (
                                    <div key={item.name} className="space-y-1">
                                        <div
                                            className={`group relative flex items-center rounded-xl transition-all ${
                                                isParentActive
                                                    ? 'bg-brand-primary/15 text-brand-primary font-bold'
                                                    : 'text-gray-300 hover:bg-gray-800/70 hover:text-white'
                                            }`}
                                        >
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    if (isCollapsed) {
                                                        setIsCollapsed(false);
                                                        setForfaitsMenuOpen(true);
                                                    } else {
                                                        setForfaitsMenuOpen(!forfaitsMenuOpen);
                                                    }
                                                }}
                                                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors text-left ${
                                                    isCollapsed ? 'justify-center' : ''
                                                }`}
                                                title={isCollapsed ? item.name : undefined}
                                            >
                                                <span className={`shrink-0 transition-transform group-hover:scale-110 ${isParentActive ? 'text-brand-primary' : 'text-gray-400 group-hover:text-white'}`}>
                                                    {item.icon}
                                                </span>
                                                {!isCollapsed && (
                                                    <span className="flex-1 text-sm font-medium tracking-wide">
                                                        {item.name}
                                                    </span>
                                                )}
                                                {!isCollapsed && (
                                                    <span className="text-gray-400 transition-transform">
                                                        {forfaitsMenuOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                                    </span>
                                                )}
                                            </button>
                                        </div>

                                        {/* Sub-items (Abonnements & Séances) */}
                                        {forfaitsMenuOpen && !isCollapsed && (
                                            <div className="pl-4 pr-1 py-1 space-y-1 ml-3 border-l-2 border-gray-700/60">
                                                {item.subItems.map((sub: any) => (
                                                    <Link
                                                        key={sub.name}
                                                        to={sub.path}
                                                        onClick={closeMobileMenu}
                                                        className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                                                            sub.isActive
                                                                ? 'bg-brand-primary text-brand-dark font-bold shadow-xs'
                                                                : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                                                        }`}
                                                    >
                                                        <span className="shrink-0">{sub.icon}</span>
                                                        <span className="truncate">{sub.name}</span>
                                                    </Link>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            }

                            const isActive = location.pathname.startsWith(item.path);
                            return (
                                <Link
                                    key={item.path}
                                    to={item.path}
                                    onClick={closeMobileMenu}
                                    title={isCollapsed ? item.name : undefined}
                                    className={`group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                                        isCollapsed ? 'justify-center' : ''
                                    } ${
                                        isActive
                                            ? 'bg-brand-primary text-brand-dark font-bold shadow-md shadow-brand-primary/10'
                                            : 'text-gray-300 hover:bg-gray-800/70 hover:text-white'
                                    }`}
                                >
                                    <span className={`shrink-0 transition-transform group-hover:scale-110 ${isActive ? 'text-brand-dark' : 'text-gray-400 group-hover:text-white'}`}>
                                        {item.icon}
                                    </span>
                                    {!isCollapsed && (
                                        <span className="truncate tracking-wide">{item.name}</span>
                                    )}
                                </Link>
                            );
                        })}
                    </nav>
                </div>

                {/* Sidebar Footer User Info */}
                <div className="p-3 border-t border-gray-800/80 bg-[#161b22]/50 shrink-0">
                    {!isCollapsed ? (
                        <>
                            <div className="flex items-center gap-3 mb-3 px-1">
                                <div className="w-9 h-9 rounded-full bg-gradient-to-tr from-brand-primary to-yellow-400 flex items-center justify-center text-brand-dark font-black shrink-0 shadow-sm text-sm">
                                    {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
                                </div>
                                <div className="overflow-hidden">
                                    <p className="text-xs font-bold text-white truncate">{user?.firstName} {user?.lastName}</p>
                                    <p className="text-[11px] text-gray-400 capitalize truncate">{user?.role}</p>
                                </div>
                            </div>
                            <button
                                onClick={logout}
                                className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-gray-800/90 hover:bg-red-600/90 text-gray-200 hover:text-white rounded-xl transition-all text-xs font-semibold shadow-xs"
                            >
                                <LogOut size={15} />
                                <span>Déconnexion</span>
                            </button>
                        </>
                    ) : (
                        <div className="flex flex-col items-center gap-2">
                            <div
                                className="w-9 h-9 rounded-full bg-gradient-to-tr from-brand-primary to-yellow-400 flex items-center justify-center text-brand-dark font-black shrink-0 shadow-sm text-xs"
                                title={`${user?.firstName} ${user?.lastName} (${user?.role})`}
                            >
                                {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
                            </div>
                            <button
                                onClick={logout}
                                className="w-9 h-9 flex items-center justify-center bg-gray-800 hover:bg-red-600 text-gray-300 hover:text-white rounded-xl transition-colors"
                                title="Déconnexion"
                            >
                                <LogOut size={16} />
                            </button>
                        </div>
                    )}
                </div>
            </aside>

            {/* Main Content */}
            <main className="flex-1 flex flex-col min-w-0 h-full overflow-hidden">
                {/* Mobile Header (Hidden on Desktop) */}
                <header className="md:hidden shrink-0 h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4 sticky top-0 z-30">
                    <span className="text-xl font-black text-brand-dark">HOP<span className="text-brand-primary">FITNESS</span></span>
                    <button
                        onClick={() => setIsMobileMenuOpen(true)}
                        className="p-2 -mr-2 text-gray-600 hover:bg-gray-100 rounded-md"
                    >
                        <Menu size={24} />
                    </button>
                </header>

                {/* Page Content */}
                <div className="flex-1 overflow-y-auto p-2.5 sm:p-4 md:p-6 w-full relative">
                    <Outlet />
                </div>
            </main>
        </div>
    );
};

export default MainLayout;
